import { useContactInvalidation } from "@/hooks/use-contact-invalidation";
import {
  createContactWebsiteV1,
  deleteContactWebsiteV1,
  updateContactWebsiteV1,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static/query-keys";
import { type APIError, type APIResponse, type Contact } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { orderBy } from "lodash";
import toast from "react-hot-toast";
import { type ContactSubitemMutationOptions } from "./contact-subitem-mutation-options";

export const useCreateContactWebsite = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof createContactWebsiteV1>[0]) =>
      createContactWebsiteV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          const oldWebsites = oldData.data.websites;

          return {
            data: {
              ...oldData.data,
              websites: orderBy([...oldWebsites, data], ["desc"]),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error creating contact website");
    },

    onSettled: async () => {
      await invalidateContact();
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact website created successfully");
      }
    },
  });
};

export const useUpdateContactWebsite = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateContactWebsiteV1>[0]) =>
      updateContactWebsiteV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          const oldWebsites = oldData.data.websites;

          return {
            data: {
              ...oldData.data,
              websites: orderBy(
                oldWebsites.map((website) => {
                  return website.id === data.id ? data : website;
                }),
                ["desc"],
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (error: AxiosError<APIError>, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating contact website");
    },

    onSettled: async () => {
      await invalidateContact();
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact website updated successfully");
      }
    },
  });
};

export const useDeleteContactWebsite = (
  options?: ContactSubitemMutationOptions,
) => {
  const queryClient = useQueryClient();
  const invalidateContact = useContactInvalidation();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteContactWebsiteV1>[0]) =>
      deleteContactWebsiteV1(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.contact, data.contactId],
        (oldData: APIResponse<Contact> | undefined) => {
          if (!oldData) return;
          return {
            data: {
              ...oldData.data,
              websites: oldData.data.websites.filter((website) => {
                return website.id !== data.id;
              }),
              ...data,
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([
          queryKeys.contact,
          data.contactId,
        ]),
      };
    },
    onError: (_error, variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.contact, variables.contactId],
          context.previousData,
        );
      }
      toast.error("Error deleting contact website");
    },

    onSettled: async () => {
      await invalidateContact();
    },
    onSuccess: () => {
      if (options?.showSuccessToast !== false) {
        toast.success("Contact website deleted successfully");
      }
    },
  });
};
